import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Building2,
  Phone,
  Mail,
  Briefcase,
  Calendar,
  Clock,
  ExternalLink
} from 'lucide-react';
import { getStatusConfig } from '../../lib/appointmentStatus';

interface RdvDetail {
  id: string;
  prospect_name: string;
  company_name?: string;
  job_title?: string;
  phone?: string;
  email?: string;
  date: string;
  time: string;
  client_name: string;
  sdr_name: string;
  status: string;
  prospect_linkedin?: string;
  created_at: string;
}

interface RdvDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  rdv: RdvDetail | null;
}

const RdvDetailModal: React.FC<RdvDetailModalProps> = ({ isOpen, onClose, rdv }) => {
  if (!rdv) return null;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const config = getStatusConfig(status);
    return `${config.bg} ${config.color} ${config.border}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={onClose}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-cyan-500 to-blue-500 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2 rounded-xl">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Détails du RDV</h2>
                      <p className="text-sm text-white/80 mt-1">Informations complètes</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                  >
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div
                    className={`inline-flex items-center px-4 py-2 rounded-xl border-2 font-semibold ${getStatusColor(
                      rdv.status
                    )}`}
                  >
                    {getStatusConfig(rdv.status).label}
                  </div>
                  <div className="text-xs text-gray-500">
                    Créé le {formatDateTime(rdv.created_at)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                        Prospect
                      </p>
                      <div className="flex items-center space-x-2">
                        <p className="text-lg font-bold text-gray-900">{rdv.prospect_name}</p>
                        {rdv.prospect_linkedin && (
                          <a
                            href={rdv.prospect_linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-600 transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {rdv.company_name && (
                    <div className="flex items-start space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <Building2 className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                          Entreprise
                        </p>
                        <p className="text-lg font-semibold text-gray-900">{rdv.company_name}</p>
                      </div>
                    </div>
                  )}

                  {rdv.job_title && (
                    <div className="flex items-start space-x-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <Briefcase className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                          Poste
                        </p>
                        <p className="text-lg font-semibold text-gray-900">{rdv.job_title}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rdv.phone && (
                    <div className="bg-white border-2 border-gray-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                          Téléphone
                        </p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">{rdv.phone}</p>
                    </div>
                  )}

                  {rdv.email && (
                    <div className="bg-white border-2 border-gray-100 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                          Email
                        </p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">{rdv.email}</p>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <Calendar className="h-4 w-4 text-cyan-600" />
                        <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">
                          Date du RDV
                        </p>
                      </div>
                      <p className="text-base font-bold text-gray-900">{formatDate(rdv.date)}</p>
                    </div>

                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className="h-4 w-4 text-cyan-600" />
                        <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">
                          Heure
                        </p>
                      </div>
                      <p className="text-base font-bold text-gray-900">{rdv.time}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-100 rounded-xl p-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                        Client
                      </p>
                      <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-xl font-semibold border border-blue-100">
                        {rdv.client_name}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                        SDR assigné
                      </p>
                      <div className="inline-flex items-center bg-cyan-50 text-cyan-700 px-4 py-2 rounded-xl font-semibold border border-cyan-100">
                        {rdv.sdr_name}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-100">
                <button
                  onClick={onClose}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RdvDetailModal;
